import { useRef } from 'react';
import { Printer, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

/**
 * OrderBill – Tax Invoice dialog
 *
 * Props:
 *   order      – order object
 *   open       – boolean
 *   onClose    – fn
 *   isAdmin    – boolean (shows Print button for admin)
 *   language   – 'en' | 'hi'
 *   bizInfo    – object from /api/admin/settings
 *                { business_name, business_address, business_phone,
 *                  business_email, gstin }
 *   thermalMode – boolean  (default false → A4; true → 80 mm thermal)
 */
export default function OrderBill({
  order,
  open,
  onClose,
  isAdmin = false,
  language = 'en',
  bizInfo = {},
  thermalMode = true,   // default: 80mm thermal — change to false for A4
}) {
  const billRef = useRef(null);
  if (!order) return null;

  /* ── Date formatter ── */
  const formatDate = (ds) =>
    new Date(ds).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

  /* ── Item partitions ── */
  const activeItems  = order.items?.filter((i) => !i.skipped) || [];
  const skippedItems = order.items?.filter((i) =>  i.skipped) || [];
  const hasHsn       = activeItems.some((i) => i.hsn_code);

  /* ── GST calculation (prices are GST-inclusive) ──
       taxable  = lineAmt / (1 + gst/100 + cess/100)
       cgst     = taxable × (gst/2) / 100
       sgst     = taxable × (gst/2) / 100
       cess     = taxable × cess    / 100
  ── */
  const computeItemTax = (item) => {
    const gstRate  = item.hsn_gst_rate  ?? 0;
    const cessRate = item.hsn_cess_rate ?? 0;
    const lineAmt  = item.price * item.quantity;
    const totalRate = 1 + gstRate / 100 + cessRate / 100;
    const taxable  = lineAmt / totalRate;
    const cgst     = taxable * (gstRate / 2) / 100;
    const sgst     = taxable * (gstRate / 2) / 100;
    const cess     = taxable * cessRate / 100;
    return { taxable, cgst, sgst, cess, lineAmt };
  };

  /* ── HSN-wise tax summary ── */
  const hsnSummaryMap = {};
  activeItems.forEach((item) => {
    if (!item.hsn_code) return;
    const t = computeItemTax(item);
    if (!hsnSummaryMap[item.hsn_code]) {
      hsnSummaryMap[item.hsn_code] = {
        code: item.hsn_code,
        description: item.hsn_description || '',
        gst_rate:  item.hsn_gst_rate  ?? 0,
        cess_rate: item.hsn_cess_rate ?? 0,
        taxable: 0, cgst: 0, sgst: 0, cess: 0, total: 0,
      };
    }
    const s = hsnSummaryMap[item.hsn_code];
    s.taxable += t.taxable;
    s.cgst    += t.cgst;
    s.sgst    += t.sgst;
    s.cess    += t.cess;
    s.total   += t.lineAmt;
  });
  const hsnSummary = Object.values(hsnSummaryMap);

  /* ── Grand totals ── */
  const grandTaxable   = hsnSummary.reduce((s, r) => s + r.taxable, 0);
  const grandCgst      = hsnSummary.reduce((s, r) => s + r.cgst,    0);
  const grandSgst      = hsnSummary.reduce((s, r) => s + r.sgst,    0);
  const grandCess      = hsnSummary.reduce((s, r) => s + r.cess,    0);
  const subtotal       = order.subtotal        ?? activeItems.reduce((s, i) => s + i.price * i.quantity, 0);
  const deliveryCharge = order.delivery_charge ?? 0;
  const grandTotal     = order.total           ?? subtotal + deliveryCharge;

  /* ── Business info – prefer bizInfo prop, fall back to sensible defaults ── */
  const biz = {
    name:    (bizInfo?.business_name    || '').trim() || 'AV MART SHAMLI',
    address: (bizInfo?.business_address || '').trim() || 'Shamli, Uttar Pradesh - 247776',
    phone:   (bizInfo?.business_phone   || '').trim() || '+91-7251972000',
    email:   (bizInfo?.business_email   || '').trim() || 'support@avmartshamli.com',
    gstin:   (bizInfo?.gstin            || '').trim(),
  };

  const r   = (n) => Number(n).toFixed(2);
  const Rs  = (n) => `&#8377;${r(n)}`;

  /* ═══════════════════════════════════════════════════
     THERMAL (80 mm) vs A4 CSS
     80 mm paper → usable width ≈ 72 mm at 8 mm margins
     Point-of-sale printers typically render at 72 dpi
  ═══════════════════════════════════════════════════ */
  const thermalCss = `
    * { margin:0; padding:0; box-sizing:border-box; }
    body {
      font-family: 'Courier New', Courier, monospace;
      font-size: 10px;
      width: 72mm;
      padding: 2mm 3mm;
      color: #000;
      background: #fff;
    }
    h1  { font-size:13px; font-weight:bold; }
    h2  { font-size:11px; font-weight:bold; }
    h3  { font-size:10px; font-weight:bold; margin-bottom:2px; }
    .center  { text-align:center; }
    .right   { text-align:right; }
    .bold    { font-weight:bold; }
    .small   { font-size:8px; }
    .divider { border-top:1px dashed #000; margin:3px 0; }
    .solid   { border-top:2px solid #000; margin:3px 0; }
    .thin    { border-top:1px solid #000; margin:3px 0; }
    table    { width:100%; border-collapse:collapse; margin:2px 0; font-size:9px; }
    th       { padding:2px 2px; font-weight:bold; font-size:9px; border-bottom:1px solid #000; }
    td       { padding:1px 2px; vertical-align:top; }
    .num     { text-align:right; }
    .ctr     { text-align:center; }
    .total-row td { font-weight:bold; border-top:1px solid #000; }
    .grand-row td { font-weight:bold; font-size:11px; border-top:2px solid #000; }
    .skipped  { color:#aaa; text-decoration:line-through; font-size:8px; }
    .two-col  { display:flex; gap:6px; margin-bottom:4px; }
    .two-col > div { flex:1; min-width:0; }
    @media print {
      body { width:72mm; }
      @page { margin:2mm 3mm; size:80mm auto; }
    }
  `;

  const a4Css = `
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Courier New',monospace; font-size:11px; width:210mm; padding:8mm; color:#000; }
    h1  { font-size:18px; font-weight:bold; }
    h2  { font-size:13px; font-weight:bold; }
    h3  { font-size:11px; font-weight:bold; margin-bottom:3px; }
    .center  { text-align:center; }
    .right   { text-align:right; }
    .bold    { font-weight:bold; }
    .small   { font-size:9px; }
    .divider { border-top:1px dashed #000; margin:5px 0; }
    .solid   { border-top:2px solid #000; margin:4px 0; }
    .thin    { border-top:1px solid #000; margin:4px 0; }
    table    { width:100%; border-collapse:collapse; margin:4px 0; font-size:10px; }
    th       { background:#f0f0f0; padding:3px 4px; border:1px solid #ccc; font-weight:bold; font-size:10px; }
    td       { padding:3px 4px; border:1px solid #ddd; vertical-align:top; }
    .num     { text-align:right; }
    .ctr     { text-align:center; }
    .mono    { font-family:monospace; }
    .tax-hdr { background:#e8e8e8; }
    .total-row td { font-weight:bold; background:#f5f5f5; }
    .grand-row td { font-weight:bold; font-size:12px; background:#e0e0e0; }
    .skipped { color:#aaa; text-decoration:line-through; font-size:9px; }
    .badge   { border:1px solid #333; padding:1px 4px; font-size:9px; display:inline-block; }
    .two-col { display:flex; gap:16px; margin-bottom:6px; }
    .two-col > div { flex:1; }
    @media print {
      body { width:210mm; }
      @page { margin:8mm; size:A4; }
    }
  `;

  const printStyles = thermalMode ? thermalCss : a4Css;

  /* ─── Thermal-friendly bill body ─── */
  const thermalBillHtml = `
    <!-- HEADER -->
    <div class="center" style="border-bottom:2px solid #000;padding-bottom:4px;margin-bottom:4px;">
      <h1>${biz.name}</h1>
      <div class="small">${biz.address}</div>
      <div class="small">Ph: ${biz.phone}</div>
      <div class="small">${biz.email}</div>
      ${biz.gstin ? `<div class="bold small" style="margin-top:2px;">GSTIN: ${biz.gstin}</div>` : ''}
    </div>
    <div class="center bold" style="font-size:11px;letter-spacing:1px;margin:3px 0;">TAX INVOICE</div>

    <!-- ORDER INFO (stacked, no two-col for narrow paper) -->
    <div class="thin"></div>
    <table style="border:none;">
      <tr><td style="border:none;padding:1px 0;width:55px;"><b>Inv #:</b></td><td style="border:none;padding:1px 0;">${order.id?.slice(0,8).toUpperCase()}</td></tr>
      <tr><td style="border:none;padding:1px 0;"><b>Date:</b></td><td style="border:none;padding:1px 0;">${formatDate(order.created_at)}</td></tr>
      <tr><td style="border:none;padding:1px 0;"><b>Pay:</b></td><td style="border:none;padding:1px 0;">Cash on Delivery</td></tr>
      <tr><td style="border:none;padding:1px 0;"><b>Status:</b></td><td style="border:none;padding:1px 0;text-transform:uppercase;font-weight:bold;">${order.status}</td></tr>
    </table>
    <div class="thin"></div>
    <b>Bill To:</b>
    <table style="border:none;">
      <tr><td style="border:none;padding:1px 0;">${order.user_name || 'Customer'}</td></tr>
      <tr><td style="border:none;padding:1px 0;">Ph: ${order.phone}</td></tr>
      <tr><td style="border:none;padding:1px 0;">${order.delivery_address}, ${order.delivery_pincode}</td></tr>
    </table>
    <div class="thin"></div>

    <!-- ITEMS (simplified for narrow paper) -->
    <b>Items</b>
    <table>
      <thead>
        <tr>
          <th style="text-align:left;">Description</th>
          <th class="ctr">Qty</th>
          <th class="num">Rate</th>
          <th class="num">Amt</th>
        </tr>
      </thead>
      <tbody>
        ${activeItems.map((item) => {
          const t = computeItemTax(item);
          return `<tr>
            <td>${item.name}${item.hsn_code ? `<br><span style="font-size:8px;color:#555;">HSN:${item.hsn_code}${item.hsn_gst_rate != null ? ' GST:'+item.hsn_gst_rate+'%' : ''}</span>` : ''}</td>
            <td class="ctr">${item.quantity}</td>
            <td class="num">${Rs(item.price)}</td>
            <td class="num bold">${Rs(t.lineAmt)}</td>
          </tr>`;
        }).join('')}
        ${skippedItems.length > 0 ? `
          <tr><td colspan="4" class="small" style="color:#aaa;font-style:italic;padding:2px;">
            Not charged: ${skippedItems.map((i) => `${i.name}×${i.quantity}`).join(', ')}
          </td></tr>` : ''}
      </tbody>
    </table>

    <!-- TOTALS -->
    <div class="thin"></div>
    <table style="border:none;">
      <tr><td style="border:none;padding:1px 2px;">Subtotal:</td><td class="num bold" style="border:none;padding:1px 2px;">${Rs(subtotal)}</td></tr>
      ${hasHsn && grandCgst > 0 ? `<tr><td style="border:none;padding:1px 2px;">CGST:</td><td class="num" style="border:none;padding:1px 2px;">${Rs(grandCgst)}</td></tr>` : ''}
      ${hasHsn && grandSgst > 0 ? `<tr><td style="border:none;padding:1px 2px;">SGST:</td><td class="num" style="border:none;padding:1px 2px;">${Rs(grandSgst)}</td></tr>` : ''}
      ${hasHsn && grandCess > 0 ? `<tr><td style="border:none;padding:1px 2px;">Cess:</td><td class="num" style="border:none;padding:1px 2px;">${Rs(grandCess)}</td></tr>` : ''}
      <tr><td style="border:none;padding:1px 2px;">Delivery:</td><td class="num" style="border:none;padding:1px 2px;">${deliveryCharge > 0 ? Rs(deliveryCharge) : 'FREE'}</td></tr>
    </table>
    <div class="solid"></div>
    <table style="border:none;">
      <tr>
        <td style="border:none;padding:2px;font-weight:bold;font-size:12px;">TOTAL:</td>
        <td class="num bold" style="border:none;padding:2px;font-size:12px;">${Rs(grandTotal)}</td>
      </tr>
    </table>

    ${hsnSummary.length > 0 ? `
    <div class="thin"></div>
    <b class="small">GST Summary</b>
    <table>
      <thead>
        <tr>
          <th style="text-align:left;">HSN</th>
          <th class="num">Taxable</th>
          <th class="num">CGST</th>
          <th class="num">SGST</th>
          ${grandCess > 0 ? '<th class="num">Cess</th>' : ''}
        </tr>
      </thead>
      <tbody>
        ${hsnSummary.map((row) => `
          <tr>
            <td>${row.code}<br><span style="font-size:7px;">${(row.gst_rate/2).toFixed(1)}%+${(row.gst_rate/2).toFixed(1)}%</span></td>
            <td class="num">${Rs(row.taxable)}</td>
            <td class="num">${Rs(row.cgst)}</td>
            <td class="num">${Rs(row.sgst)}</td>
            ${grandCess > 0 ? `<td class="num">${row.cess > 0 ? Rs(row.cess) : '—'}</td>` : ''}
          </tr>`).join('')}
        <tr class="total-row">
          <td><b>Total</b></td>
          <td class="num"><b>${Rs(grandTaxable)}</b></td>
          <td class="num"><b>${Rs(grandCgst)}</b></td>
          <td class="num"><b>${Rs(grandSgst)}</b></td>
          ${grandCess > 0 ? `<td class="num"><b>${Rs(grandCess)}</b></td>` : ''}
        </tr>
      </tbody>
    </table>` : ''}

    ${order.notes ? `<div class="thin"></div><div class="small"><b>Notes:</b> ${order.notes}</div>` : ''}
    ${order.return_status ? `<div class="small"><b>Return:</b> ${order.return_status.replace(/_/g,' ')}${order.is_partial_return ? ' (Partial)' : ''}</div>` : ''}

    <div class="divider" style="margin-top:6px;"></div>
    <div class="center small">Computer-generated. No signature required.</div>
    <div class="center small">Thank you! | धन्यवाद!</div>
  `;

  /* ─── A4 bill body ─── */
  const a4BillHtml = `
    <!-- HEADER -->
    <div class="center" style="border-bottom:2px solid #000;padding-bottom:6px;margin-bottom:6px;">
      <h1>${biz.name}</h1>
      <div class="small">${biz.address}</div>
      <div class="small">Ph: ${biz.phone} | ${biz.email}</div>
      ${biz.gstin ? `<div class="bold" style="margin-top:3px;">GSTIN: ${biz.gstin}</div>` : ''}
    </div>
    <div class="center bold" style="font-size:14px;letter-spacing:2px;margin:4px 0;">TAX INVOICE</div>

    <div class="two-col" style="margin-top:6px;">
      <div>
        <h3>Invoice Details</h3>
        <table style="border:none;">
          <tr><td style="border:none;padding:1px 0;width:90px;"><b>Invoice No:</b></td><td style="border:none;padding:1px 0;">#${order.id?.slice(0,8).toUpperCase()}</td></tr>
          <tr><td style="border:none;padding:1px 0;"><b>Date:</b></td><td style="border:none;padding:1px 0;">${formatDate(order.created_at)}</td></tr>
          <tr><td style="border:none;padding:1px 0;"><b>Payment:</b></td><td style="border:none;padding:1px 0;">Cash on Delivery</td></tr>
          <tr><td style="border:none;padding:1px 0;"><b>Status:</b></td><td style="border:none;padding:1px 0;text-transform:uppercase;font-weight:bold;">${order.status}</td></tr>
        </table>
      </div>
      <div>
        <h3>Bill To</h3>
        <table style="border:none;">
          <tr><td style="border:none;padding:1px 0;width:60px;"><b>Name:</b></td><td style="border:none;padding:1px 0;">${order.user_name || 'Customer'}</td></tr>
          <tr><td style="border:none;padding:1px 0;"><b>Phone:</b></td><td style="border:none;padding:1px 0;">${order.phone}</td></tr>
          <tr><td style="border:none;padding:1px 0;"><b>Address:</b></td><td style="border:none;padding:1px 0;">${order.delivery_address}, ${order.delivery_pincode}</td></tr>
        </table>
      </div>
    </div>

    <div class="thin"></div>
    <h3>Items</h3>
    <table>
      <thead>
        <tr>
          <th class="ctr" style="width:18px;">#</th>
          ${hasHsn ? '<th style="width:46px;">HSN/SAC</th>' : ''}
          <th>Description</th>
          <th class="ctr" style="width:28px;">Qty</th>
          <th class="num" style="width:52px;">Unit Price</th>
          ${hasHsn ? '<th class="num" style="width:56px;">Taxable Amt</th>' : ''}
          ${hasHsn ? '<th class="num" style="width:44px;">GST%</th>' : ''}
          <th class="num" style="width:60px;">Total</th>
        </tr>
      </thead>
      <tbody>
        ${activeItems.map((item, idx) => {
          const t = computeItemTax(item);
          return `<tr>
            <td class="ctr">${idx + 1}</td>
            ${hasHsn ? `<td class="mono ctr">${item.hsn_code || '—'}</td>` : ''}
            <td>${item.name}</td>
            <td class="ctr">${item.quantity}</td>
            <td class="num">${Rs(item.price)}</td>
            ${hasHsn ? `<td class="num">${Rs(t.taxable)}</td>` : ''}
            ${hasHsn ? `<td class="ctr">${item.hsn_gst_rate != null ? item.hsn_gst_rate + '%' : '—'}</td>` : ''}
            <td class="num bold">${Rs(t.lineAmt)}</td>
          </tr>`;
        }).join('')}
        ${skippedItems.length > 0 ? `
          <tr><td colspan="10" style="padding:2px 4px;font-size:9px;color:#aaa;font-style:italic;">
            Unavailable (not charged): ${skippedItems.map((i) => `${i.name} x${i.quantity}`).join(', ')}
          </td></tr>` : ''}
      </tbody>
    </table>

    <div style="display:flex;justify-content:flex-end;margin-top:4px;">
      <table style="width:260px;font-size:10px;">
        <tr><td style="border:none;padding:2px 4px;">Items Subtotal:</td><td class="num bold" style="border:none;padding:2px 4px;">${Rs(subtotal)}</td></tr>
        ${hasHsn && grandCgst > 0 ? `<tr><td style="border:none;padding:2px 4px;">CGST:</td><td class="num" style="border:none;padding:2px 4px;">${Rs(grandCgst)}</td></tr>` : ''}
        ${hasHsn && grandSgst > 0 ? `<tr><td style="border:none;padding:2px 4px;">SGST:</td><td class="num" style="border:none;padding:2px 4px;">${Rs(grandSgst)}</td></tr>` : ''}
        ${hasHsn && grandCess > 0 ? `<tr><td style="border:none;padding:2px 4px;">Cess:</td><td class="num" style="border:none;padding:2px 4px;">${Rs(grandCess)}</td></tr>` : ''}
        <tr><td style="border:none;padding:2px 4px;">Delivery:</td><td class="num" style="border:none;padding:2px 4px;">${deliveryCharge > 0 ? Rs(deliveryCharge) : '<span style="color:#16a34a;font-weight:bold;">FREE</span>'}</td></tr>
        ${skippedItems.length > 0 ? `<tr><td style="border:none;padding:2px 4px;color:#aaa;font-size:9px;">Skipped items deducted:</td><td class="num" style="border:none;padding:2px 4px;color:#aaa;font-size:9px;">&#8722;${Rs(skippedItems.reduce((s, i) => s + i.price * i.quantity, 0))}</td></tr>` : ''}
        <tr style="border-top:2px solid #000;">
          <td style="border:none;padding:3px 4px;font-weight:bold;font-size:13px;">GRAND TOTAL:</td>
          <td class="num bold" style="border:none;padding:3px 4px;font-size:13px;">${Rs(grandTotal)}</td>
        </tr>
      </table>
    </div>

    ${hsnSummary.length > 0 ? `
    <div class="thin" style="margin-top:8px;"></div>
    <h3>HSN-wise Tax Summary</h3>
    <table>
      <thead>
        <tr class="tax-hdr">
          <th>HSN/SAC</th><th>Description</th><th class="num">Taxable Value</th>
          <th class="ctr">CGST Rate</th><th class="num">CGST Amt</th>
          <th class="ctr">SGST Rate</th><th class="num">SGST Amt</th>
          <th class="ctr">Cess Rate</th><th class="num">Cess Amt</th>
          <th class="num">Total Tax</th>
        </tr>
      </thead>
      <tbody>
        ${hsnSummary.map((row) => `
          <tr>
            <td class="mono ctr">${row.code}</td>
            <td>${row.description}</td>
            <td class="num">${Rs(row.taxable)}</td>
            <td class="ctr">${(row.gst_rate / 2).toFixed(2)}%</td>
            <td class="num">${Rs(row.cgst)}</td>
            <td class="ctr">${(row.gst_rate / 2).toFixed(2)}%</td>
            <td class="num">${Rs(row.sgst)}</td>
            <td class="ctr">${row.cess_rate > 0 ? row.cess_rate.toFixed(2) + '%' : '—'}</td>
            <td class="num">${row.cess > 0 ? Rs(row.cess) : '—'}</td>
            <td class="num bold">${Rs(row.cgst + row.sgst + row.cess)}</td>
          </tr>`).join('')}
        <tr class="total-row">
          <td colspan="2" class="bold">Total</td>
          <td class="num">${Rs(grandTaxable)}</td>
          <td class="ctr">—</td><td class="num">${Rs(grandCgst)}</td>
          <td class="ctr">—</td><td class="num">${Rs(grandSgst)}</td>
          <td class="ctr">—</td>
          <td class="num">${grandCess > 0 ? Rs(grandCess) : '—'}</td>
          <td class="num">${Rs(grandCgst + grandSgst + grandCess)}</td>
        </tr>
      </tbody>
    </table>` : ''}

    ${order.notes || order.return_status ? `
    <div class="thin"></div>
    ${order.notes ? `<div class="small" style="margin:2px 0;"><b>Notes:</b> ${order.notes}</div>` : ''}
    ${order.return_status ? `<div class="small" style="margin:2px 0;"><b>Return Status:</b> ${order.return_status.replace(/_/g, ' ')}${order.is_partial_return ? ' (Partial)' : ''}</div>` : ''}` : ''}

    <div class="divider" style="margin-top:10px;"></div>
    <div class="center small">This is a computer-generated invoice. No signature required.</div>
    <div class="center small" style="margin-top:2px;">Thank you for shopping with ${biz.name}! | खरीदारी के लिए धन्यवाद!</div>
  `;

  const billHtml = thermalMode ? thermalBillHtml : a4BillHtml;

  // @page is already embedded inside thermalCss / a4Css — no duplicate needed
  const fullHtml = (body) =>
    `<!DOCTYPE html><html><head><meta charset="utf-8">` +
    `<title>Tax Invoice #${order.id?.slice(0, 8)}</title>` +
    `<style>${printStyles}</style>` +
    `</head><body>${body}</body></html>`;

  /* ── Actions ── */
  const handlePrint = () => {
    const w = window.open('', '_blank', 'width=900,height=700');
    w.document.write(
      fullHtml(
        billHtml +
        `<script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};<\/script>`
      )
    );
    w.document.close();
  };

  const handleDownload = () => {
    const blob = new Blob([fullHtml(billHtml)], { type: 'text/html' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `TaxInvoice-${order.id?.slice(0, 8)}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── Screen preview helpers ── */
  const th = { border: '1px solid #cbd5e1', padding: '3px 5px', textAlign: 'center', fontWeight: 'bold' };
  const td = { border: '1px solid #e2e8f0', padding: '3px 5px', textAlign: 'center' };

  const PreviewItems = () => (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px', marginBottom: '6px' }}>
      <thead>
        <tr style={{ background: '#f1f5f9' }}>
          <th style={th}>#</th>
          {hasHsn && <th style={th}>HSN</th>}
          <th style={{ ...th, textAlign: 'left' }}>Description</th>
          <th style={th}>Qty</th>
          <th style={{ ...th, textAlign: 'right' }}>Rate</th>
          {hasHsn && <th style={{ ...th, textAlign: 'right' }}>Taxable</th>}
          {hasHsn && <th style={th}>GST%</th>}
          <th style={{ ...th, textAlign: 'right' }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {activeItems.map((item, idx) => {
          const t = computeItemTax(item);
          return (
            <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
              <td style={td}>{idx + 1}</td>
              {hasHsn && <td style={{ ...td, textAlign: 'center', fontFamily: 'monospace', fontSize: '10px' }}>{item.hsn_code || '—'}</td>}
              <td style={{ ...td, textAlign: 'left' }}>{item.name}</td>
              <td style={td}>{item.quantity}</td>
              <td style={{ ...td, textAlign: 'right' }}>₹{r(item.price)}</td>
              {hasHsn && <td style={{ ...td, textAlign: 'right' }}>₹{r(t.taxable)}</td>}
              {hasHsn && <td style={td}>{item.hsn_gst_rate != null ? item.hsn_gst_rate + '%' : '—'}</td>}
              <td style={{ ...td, textAlign: 'right', fontWeight: 'bold' }}>₹{r(t.lineAmt)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{language === 'hi' ? 'कर चालान' : 'Tax Invoice'}</DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 mb-4">
          {isAdmin && (
            <Button onClick={handlePrint} className="flex-1 bg-blue-600 hover:bg-blue-700">
              <Printer className="w-4 h-4 mr-2" />
              {language === 'hi' ? 'प्रिंट करें' : 'Print Invoice'}
            </Button>
          )}
          <Button onClick={handleDownload} variant="outline" className="flex-1">
            <Download className="w-4 h-4 mr-2" />
            {language === 'hi' ? 'डाउनलोड' : 'Download'}
          </Button>
        </div>

        {/* ── Screen Preview ── */}
        <div
          className="border border-slate-200 rounded-lg p-4 bg-white text-xs font-mono overflow-x-auto"
          ref={billRef}
        >
          {/* Header */}
          <div className="text-center border-b-2 border-black pb-2 mb-3">
            <div className="text-base font-bold">{biz.name}</div>
            <div className="text-slate-500">{biz.address}</div>
            <div className="text-slate-500">Ph: {biz.phone}</div>
            <div className="text-slate-500">{biz.email}</div>
            {biz.gstin && <div className="font-bold mt-1">GSTIN: {biz.gstin}</div>}
          </div>
          <div className="text-center font-bold text-sm tracking-widest mb-3">TAX INVOICE</div>

          {/* Order + Customer (two-col for screen, single-col for thermal) */}
          <div className="grid grid-cols-2 gap-4 mb-3 text-xs">
            <div>
              <div className="font-bold mb-1">Invoice Details</div>
              <div>Invoice No: <span className="font-bold">#{order.id?.slice(0, 8).toUpperCase()}</span></div>
              <div>Date: {formatDate(order.created_at)}</div>
              <div>Payment: Cash on Delivery</div>
              <div>Status: <span className="uppercase font-bold">{order.status}</span></div>
            </div>
            <div>
              <div className="font-bold mb-1">Bill To</div>
              <div>{order.user_name || 'Customer'}</div>
              <div>Ph: {order.phone}</div>
              <div>{order.delivery_address}, {order.delivery_pincode}</div>
            </div>
          </div>

          <div className="border-t border-slate-300 my-2" />

          {/* Items */}
          <div className="font-bold mb-1">Items</div>
          <PreviewItems />

          {/* Skipped items note */}
          {skippedItems.length > 0 && (
            <div className="text-slate-400 text-xs italic mb-2">
              Not charged (unavailable): {skippedItems.map((i) => `${i.name} ×${i.quantity}`).join(', ')}
            </div>
          )}

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-56 text-xs space-y-0.5">
              <div className="flex justify-between py-0.5 text-slate-600">
                <span>Subtotal:</span><span>₹{r(subtotal)}</span>
              </div>
              {hasHsn && grandCgst > 0 && <div className="flex justify-between py-0.5"><span>CGST:</span><span>₹{r(grandCgst)}</span></div>}
              {hasHsn && grandSgst > 0 && <div className="flex justify-between py-0.5"><span>SGST:</span><span>₹{r(grandSgst)}</span></div>}
              {hasHsn && grandCess > 0 && <div className="flex justify-between py-0.5"><span>Cess:</span><span>₹{r(grandCess)}</span></div>}
              <div className="flex justify-between py-0.5">
                <span>Delivery:</span>
                <span className={deliveryCharge > 0 ? 'font-medium' : 'text-green-600 font-medium'}>
                  {deliveryCharge > 0 ? `₹${r(deliveryCharge)}` : 'FREE'}
                </span>
              </div>
              {skippedItems.length > 0 && (
                <div className="flex justify-between py-0.5 text-slate-400 line-through">
                  <span>Skipped:</span>
                  <span>-₹{r(skippedItems.reduce((s, i) => s + i.price * i.quantity, 0))}</span>
                </div>
              )}
              <div className="flex justify-between py-1 border-t-2 border-black font-bold text-sm mt-1">
                <span>GRAND TOTAL:</span><span>₹{r(grandTotal)}</span>
              </div>
            </div>
          </div>

          {/* HSN Tax Summary */}
          {hsnSummary.length > 0 && (
            <>
              <div className="border-t border-slate-300 my-2" />
              <div className="font-bold mb-1">HSN-wise Tax Summary</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10px' }}>
                <thead>
                  <tr style={{ background: '#f1f5f9' }}>
                    {['HSN', 'Description', 'Taxable Val', 'CGST%', 'CGST Amt', 'SGST%', 'SGST Amt', 'Cess%', 'Cess Amt', 'Total Tax'].map((h) => (
                      <th key={h} style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'center', fontWeight: 'bold' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {hsnSummary.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'center', fontFamily: 'monospace' }}>{row.code}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px' }}>{row.description}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'right' }}>₹{r(row.taxable)}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'center' }}>{(row.gst_rate / 2).toFixed(2)}%</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'right' }}>₹{r(row.cgst)}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'center' }}>{(row.gst_rate / 2).toFixed(2)}%</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'right' }}>₹{r(row.sgst)}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'center' }}>{row.cess_rate > 0 ? row.cess_rate.toFixed(2) + '%' : '—'}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'right' }}>{row.cess > 0 ? '₹' + r(row.cess) : '—'}</td>
                      <td style={{ border: '1px solid #e2e8f0', padding: '2px 4px', textAlign: 'right', fontWeight: 'bold' }}>₹{r(row.cgst + row.sgst + row.cess)}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }}>
                    <td colSpan={2} style={{ border: '1px solid #cbd5e1', padding: '2px 4px' }}>Total</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'right' }}>₹{r(grandTaxable)}</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'center' }}>—</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'right' }}>₹{r(grandCgst)}</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'center' }}>—</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'right' }}>₹{r(grandSgst)}</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'center' }}>—</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'right' }}>{grandCess > 0 ? '₹' + r(grandCess) : '—'}</td>
                    <td style={{ border: '1px solid #cbd5e1', padding: '2px 4px', textAlign: 'right' }}>₹{r(grandCgst + grandSgst + grandCess)}</td>
                  </tr>
                </tbody>
              </table>
            </>
          )}

          {/* Notes / Return Status */}
          {(order.notes || order.return_status) && (
            <div className="mt-2 text-xs text-slate-600 space-y-0.5">
              {order.notes && <div><span className="font-bold">Notes:</span> {order.notes}</div>}
              {order.return_status && (
                <div>
                  <span className="font-bold">Return:</span>{' '}
                  {order.return_status.replace(/_/g, ' ')}
                  {order.is_partial_return ? ' (Partial)' : ''}
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-dashed border-slate-400 mt-3 pt-2 text-center text-slate-500">
            <div>Computer-generated invoice. No signature required.</div>
            <div>Thank you for shopping! | खरीदारी के लिए धन्यवाद!</div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
